// useMarxentButton.js
import { ref, nextTick, computed } from 'vue';
import { useBrand } from '@composables/useBrand';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useProductFlags } from '@modules/featureControl';
import { OSVersionSupported, urlContainsMarxentCTATrue } from '@utils/OSVersionSupportedUtil';

/**
 * Composable to manage the Marxent CTA experience.
 * @param {(upcNumber: number, marxentApiCredentials: string | undefined) => Promise<{
    marxentUrl: import('vue').Ref<string | null | undefined>;
    isArAssetsAvailable: import('vue').Ref<boolean | null | undefined>;
}>} initMarxent - The function to initialize the Marxent URL.
 * @param {string | undefined} [marxentApiCredentials]
 */
function useMarxentButton(initMarxent, marxentApiCredentials) {
  const { isMcom } = useBrand();

  const { isBigTicketItemFlag } = useProductFlags();

  // Flag to control auto-start behavior.
  let shouldAutoStartMarxent = true;

  /**
 * @type {import('vue').Ref<{ marxentUrl: import('vue').Ref<string | null | undefined>,
 *    isArAssetsAvailable: import('vue').Ref<boolean | null | undefined>} | null>}
 */

  const marxentValues = ref(null);
  // Computed property to determine if the AR experience is eligible.
  const isArReady = computed(() => isMcom && isBigTicketItemFlag && OSVersionSupported());
  const isArEligible = computed(() => !!marxentValues.value?.isArAssetsAvailable);

  const { isDesktop } = useDeviceType();

  const marxentATagStyle = `
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    color: #fff;
    text-decoration: none;
    padding: 6px;`;

  const marxentButtonStyles = isDesktop.value ? 'padding: 6px;' : marxentATagStyle;

  /**
   * Fetches the Marxent URL and updates the marxentUrl ref.
   * @param {number} upcNumber
   */
  const checkForMarxentAssetsAndUrl = async (upcNumber) => {
    if (isArReady.value) {
      const { marxentUrl, isArAssetsAvailable } = await initMarxent(upcNumber, marxentApiCredentials);
      marxentValues.value = { marxentUrl, isArAssetsAvailable };
    }
  };

  /**
 * Triggers the Marxent experience by programmatically clicking the hyperlink.
 */
  const autoStartMarxentExperience = () => {
    const hasMarxentFlag = urlContainsMarxentCTATrue();
    if (hasMarxentFlag) { // when a user scans the QR code to get url, marxent experience needs to auto start
      const marxentHrefBtn = document.querySelector('.marxent');
      if (shouldAutoStartMarxent && marxentHrefBtn) {
        marxentHrefBtn.click();
        shouldAutoStartMarxent = false;
      }
    }
  };

  /**
   * Sets up auto-start for the Marxent experience if the CTA condition is met.
   * @param {number} upcNumber
   */
  const setupAutoStartMarxent = async (upcNumber) => {
    try {
      await checkForMarxentAssetsAndUrl(upcNumber);
      await nextTick();
      autoStartMarxentExperience();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  return {
    marxentValues,
    isArEligible,
    isArReady,
    setupAutoStartMarxent,
    marxentButtonStyles,
    autoStartMarxentExperience,
    checkForMarxentAssetsAndUrl,
  };
}

export default useMarxentButton;
